import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <span style="font-size: 14px;">🐾</span>
    </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
};

const statusIcons = {
  perdido: createCustomIcon('#E57373'),
  encontrado: createCustomIcon('#81C784'),
  adocao: createCustomIcon('#64B5F6'),
};

const MapPage = () => {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [center, setCenter] = useState([-15.7942, -47.8822]); // Brasília as default

  useEffect(() => {
    const fetchPets = async () => {
      try {
        const response = await axios.get(`${API}/pets`);
        setPets(response.data);
        
        // Center map on first pet if available
        if (response.data.length > 0) {
          const firstPet = response.data[0];
          setCenter([firstPet.latitude, firstPet.longitude]);
        }
      } catch (error) {
        console.error('Error fetching pets:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPets();
  }, []);

  const filteredPets = selectedStatus === 'all' 
    ? pets 
    : pets.filter(pet => pet.status === selectedStatus);

  const statusColors = {
    perdido: 'bg-[#E57373]',
    encontrado: 'bg-[#81C784]',
    adocao: 'bg-[#64B5F6]'
  };

  const statusLabels = {
    perdido: 'Perdido',
    encontrado: 'Encontrado',
    adocao: 'Para Adoção'
  };

  return (
    <div className="min-h-screen bg-[#F5D79C]">
      <Navbar />
      
      <main className="pt-24 pb-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-4xl font-display font-bold text-[#5D4A3A] mb-2">Mapa de Pets</h1>
            <p className="text-[#7A5B3E]">Visualize a localização de todos os pets cadastrados</p>
          </div>

          {/* Legend & Filters */}
          <div className="bg-[#FCEBC5] rounded-2xl shadow-lg p-4 mb-6 border border-[#C4956A]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-[#5D4A3A]">Filtrar:</span>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={selectedStatus === 'all' ? 'default' : 'outline'}
                    className={`rounded-full ${selectedStatus === 'all' ? 'bg-[#6B5344] hover:bg-[#5D4A3A]' : 'border-[#C4956A] text-[#5D4A3A]'}`}
                    onClick={() => setSelectedStatus('all')}
                  >
                    Todos ({pets.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedStatus === 'perdido' ? 'default' : 'outline'}
                    className={`rounded-full ${selectedStatus === 'perdido' ? 'bg-[#E57373] hover:bg-[#EF5350]' : 'border-[#E57373] text-[#E57373]'}`}
                    onClick={() => setSelectedStatus('perdido')}
                  >
                    Perdidos ({pets.filter(p => p.status === 'perdido').length})
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedStatus === 'encontrado' ? 'default' : 'outline'}
                    className={`rounded-full ${selectedStatus === 'encontrado' ? 'bg-[#81C784] hover:bg-[#66BB6A]' : 'border-[#81C784] text-[#81C784]'}`}
                    onClick={() => setSelectedStatus('encontrado')}
                  >
                    Encontrados ({pets.filter(p => p.status === 'encontrado').length})
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedStatus === 'adocao' ? 'default' : 'outline'}
                    className={`rounded-full ${selectedStatus === 'adocao' ? 'bg-[#64B5F6] hover:bg-[#42A5F5]' : 'border-[#64B5F6] text-[#64B5F6]'}`}
                    onClick={() => setSelectedStatus('adocao')}
                  >
                    Adoção ({pets.filter(p => p.status === 'adocao').length})
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#E57373]"></div>
                  <span className="text-[#5D4A3A]">Perdido</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#81C784]"></div>
                  <span className="text-[#5D4A3A]">Encontrado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#64B5F6]"></div>
                  <span className="text-[#5D4A3A]">Adoção</span>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="bg-[#FCEBC5] rounded-2xl shadow-lg overflow-hidden border border-[#C4956A]" style={{ height: '600px' }}>
            {loading ? (
              <div className="h-full flex items-center justify-center bg-[#F5E6C8]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#6B5344] border-t-transparent mx-auto mb-4"></div>
                  <p className="text-[#7A5B3E]">Carregando mapa...</p>
                </div>
              </div>
            ) : (
              <MapContainer
                center={center}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {filteredPets.map(pet => (
                  <Marker
                    key={pet.id}
                    position={[pet.latitude, pet.longitude]}
                    icon={statusIcons[pet.status]}
                  >
                    <Popup className="pet-popup" maxWidth={300}>
                      <div className="p-2">
                        {/* Image */}
                        {pet.imagem_url ? (
                          <img 
                            src={pet.imagem_url} 
                            alt={pet.nome}
                            className="w-full h-32 object-cover rounded-lg mb-3"
                          />
                        ) : (
                          <div className="w-full h-32 bg-[#F5E6C8] rounded-lg flex items-center justify-center mb-3">
                            <span className="text-4xl">🐾</span>
                          </div>
                        )}

                        {/* Info */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="font-bold text-[#5D4A3A]">{pet.nome}</h3>
                            <Badge className={`${statusColors[pet.status]} text-white text-xs rounded-full`}>
                              {statusLabels[pet.status]}
                            </Badge>
                          </div>

                          <p className="text-sm text-[#6B5344]">
                            {pet.especie} {pet.raca && `• ${pet.raca}`}
                          </p>

                          <p className="text-sm text-[#7A5B3E] line-clamp-2">
                            {pet.descricao}
                          </p>

                          {pet.ponto_referencia && (
                            <div className="flex items-start gap-1 text-xs text-[#7A5B3E]">
                              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>{pet.ponto_referencia}</span>
                            </div>
                          )}

                          <div className="pt-2 border-t border-[#C4956A]">
                            <p className="text-xs text-[#7A5B3E]">Contato:</p>
                            <p className="text-sm font-medium text-[#5D4A3A]">{pet.usuario_nome}</p>
                            <a 
                              href={`mailto:${pet.usuario_email}`}
                              className="text-xs text-[#6B5344] hover:underline"
                            >
                              {pet.usuario_email}
                            </a>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </div>

          {/* Empty State */}
          {!loading && filteredPets.length === 0 && (
            <div className="text-center py-8 mt-4 bg-[#FCEBC5] rounded-2xl border border-[#C4956A]">
              <span className="text-6xl">🐾</span>
              <p className="text-[#7A5B3E] mt-4 mb-4">Nenhum pet encontrado com este filtro</p>
              <Link to="/cadastrar-pet">
                <Button className="btn-primary">
                  Cadastrar Pet
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MapPage;
